import {
    Text,
    View,
    Image,
    StyleSheet,
} from '@react-pdf/renderer';

import {
    ReportPDFOverallCardInfo,
    ReportTimeRangeType,
    ReportItemSummaryType,
} from 'types/Reports';

import { getOverallUsageChartBase64PNG } from '../report/utils/reportChart';
import { getTitleByTime } from '../report/utils/reportString';

import ReportPDFOverallPercentText from './ReportPDFOverallPercentText';
import ReportPDFOverallEdgeText from './ReportPDFOverallEdgeText';
import ReportPDFOverallAverageText from './ReportPDFOverallAverageText';
import ReportPDFOverallBulletText from './ReportPDFOverallBulletText';
import ReportPDFOverallTotalText from './ReportPDFOverallTotalText';
import ReportPDFEmptyChart from './ReportPDFEmptyChart';

const overallUsageCardStyles = StyleSheet.create({
    overallCard: {
        position: 'relative',
        width: '100%',
        padding: 10,
        backgroundColor: '#F2F2F2',
    },
    overallCardTitle: {
        width: '100%',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 500,
        color: '#333333',
    },
    overallCardTotalContainer: {
        width: '100%',
        fontFamily: 'Roboto',
        display: 'flex',
        flexDirection: 'row',
        marginTop: 10,
    },
    overallCardTotalFirstSection: {
        position: 'relative',
        fontFamily: 'Roboto',
    },
    overallCardComparisonContainer: {
        width: '100%',
        position: 'relative',
        fontFamily: 'Roboto',
    },
    overallCardChart: {
        position: 'relative',
        width: '100%',
        minHeight: 130,
        height: 130,
        maxHeight: 130,
        marginTop: 10,
    }
});

type ReportPDFOverallUsageCardProps = {
    title: string;

    timeRangeType: ReportTimeRangeType;
    itemType: ReportItemSummaryType;

    item: ReportPDFOverallCardInfo;

    unit: string;

    numberOfItems: number;

    color: string;

    dateFormat: string;

    reportPeriod: string;
};

const ReportPDFOverallUsageCard = ({
    title,

    timeRangeType,
    itemType,

    item,

    unit,

    numberOfItems,

    color,

    dateFormat,

    reportPeriod,
}: ReportPDFOverallUsageCardProps) => {
    const {
        total,
        percent,
        highestItem,
        lowestItem,
        average,
        prevAverage,
        categories,
        data,
    } = item;

    const titleTimeText = getTitleByTime(timeRangeType);

    let body: JSX.Element;

    if (data.length === 0) {
        body = <ReportPDFEmptyChart />;
    } else {
        const chartImageBase64: string = getOverallUsageChartBase64PNG({
            itemType,
            chartWidth: '1070px',
            chartHeight: '280px',
            x: categories,
            y: data,
            color,
            backgroundColor: '#F2F2F2',
        });

        const chartInsights = <>
            {
                percent !== null && percent !== 0 &&
                    <ReportPDFOverallPercentText
                        itemType={itemType}
                        percent={percent}
                    />
            }
            <ReportPDFOverallEdgeText
                isHighest={true}
                itemType={itemType}
                item={highestItem}
                unit={unit}
                dateFormat={dateFormat}
                numberOfItems={numberOfItems}
            />
            {
                itemType !== 'waterLeakage' &&
                <ReportPDFOverallEdgeText
                    isHighest={false}
                    itemType={itemType}
                    item={lowestItem}
                    unit={unit}
                    dateFormat={dateFormat}
                    numberOfItems={numberOfItems}
                />
            }
            {
                itemType !== 'waterLeakage' &&
                <ReportPDFOverallAverageText
                    itemType={itemType}
                    average={average}
                    prevAverage={prevAverage}
                    unit={unit}
                    monthString={`in ${reportPeriod}`}
                />
            }
        </>;

        body = <>
            <View style={overallUsageCardStyles.overallCardComparisonContainer}>
                {chartInsights}
            </View>

            <ReportPDFOverallBulletText itemType={itemType} />

            <Image style={overallUsageCardStyles.overallCardChart} src={chartImageBase64} />
        </>;
    }

    return (
        <View style={overallUsageCardStyles.overallCard}>
            <Text style={overallUsageCardStyles.overallCardTitle}>{title}{titleTimeText}</Text>

            <ReportPDFOverallTotalText
                itemType={itemType}
                numberOfItems={numberOfItems}
                total={total}
                unit={unit}
                color={color}
            />

            {body}
        </View>
    );
};

export default ReportPDFOverallUsageCard;
